/**
 * Response Time Tracker — dedicated analytics page under Automation.
 *
 * Phase 1 of the multi-phase Response Time Tracker spec. Surfaces the data
 * already captured by services/response_time_service.py:
 *   - Today's KPIs (assigned / responded / avg / response rate)
 *   - Per-agent table (avg, fastest, slowest, total leads, response rate)
 *     with per-row colour coding green/orange/red/grey
 *   - Direct vs Indirect agent split (separate leaderboard tabs)
 *   - This Week / This Month / This Quarter time-period filter
 *   - Unresponded leads list with live wait timers
 *
 * Phases not yet built (placeholders called out in the page so the team
 * knows what's coming):
 *   - SLA rules & status badges
 *   - Escalation workflow
 *   - Auto-reassignment audit log
 *   - Source/campaign/property breakdowns
 *   - Trend charts and SLA heatmaps
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Timer, Trophy, RefreshCw, AlertTriangle, CheckCircle, Clock, Search, Plus, Trash2, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Colour mapping mirrors response_time_service.py thresholds
const colorClasses = (color) => {
  switch (color) {
    case 'green': return { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300', row: 'bg-emerald-50/40' };
    case 'orange': return { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-300', row: 'bg-orange-50/40' };
    case 'red': return { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-300', row: 'bg-red-50/40' };
    default: return { text: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-300', row: '' };
  }
};

const slaStatusBadge = (status) => {
  const map = {
    pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
    in_progress: { label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-300' },
    warning: { label: 'SLA Warning', cls: 'bg-orange-50 text-orange-700 border-orange-300' },
    breached: { label: 'SLA Breached', cls: 'bg-red-50 text-red-700 border-red-300' },
    met: { label: 'SLA Met', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
    escalated: { label: 'Escalated', cls: 'bg-purple-50 text-purple-700 border-purple-300' },
  };
  const entry = map[status];
  if (!entry) return null;
  return <Badge className={`${entry.cls} border whitespace-nowrap`}>{entry.label}</Badge>;
};

const splitCsv = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

const formatHM = (minutes) => {
  if (minutes === null || minutes === undefined) return '—';
  const m = Math.round(minutes);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

// Compute YYYY-MM-DD strings for the chosen time period.
const periodToRange = (period) => {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const from = new Date(now);
  if (period === 'week') {
    from.setDate(now.getDate() - 7);
  } else if (period === 'month') {
    from.setDate(now.getDate() - 30);
  } else {
    from.setDate(now.getDate() - 90);
  }
  return { date_from: fmt(from), date_to: fmt(now), from_obj: from, to_obj: now };
};

// Formatters used throughout the page so every visible timestamp follows the
// same locale-aware shape — short date for ranges, full date+time for events.
const fmtDateShort = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const ResponseTimeTracker = () => {
  const { token } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [unresponded, setUnresponded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [agentTab, setAgentTab] = useState('direct');
  const [search, setSearch] = useState('');
  const [rules, setRules] = useState([]);
  const [editingRule, setEditingRule] = useState(null); // null | rule object | { __new: true }
  const blankRule = {
    name: '',
    applies_to_source: [],
    applies_to_priority: [],
    applies_to_department: [],
    warning_minutes: 30,
    breach_minutes: 60,
    priority_order: 100,
    is_active: true,
  };

  const fetchRules = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/sla-rules?token=${token}`);
      const data = await res.json();
      if (data.success) setRules(data.rules || []);
    } catch (e) {
      console.error('[RT] rules fetch failed', e);
    }
  }, [token]);

  const saveRule = async (rule) => {
    try {
      const isNew = rule.__new;
      const payload = { ...rule };
      delete payload.__new;
      const url = isNew ? `${API}/api/sla-rules?token=${token}` : `${API}/api/sla-rules/${rule.id}?token=${token}`;
      const method = isNew ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.detail || data.error || 'save failed');
      toast.success(isNew ? 'Rule created' : 'Rule updated');
      setEditingRule(null);
      fetchRules();
    } catch (e) {
      toast.error(`Failed to save rule: ${e.message || e}`);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/sla-rules/${rule.id}?token=${token}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'delete failed');
      toast.success('Rule deleted');
      fetchRules();
    } catch (e) {
      toast.error(`Failed to delete rule: ${e.message || e}`);
    }
  };

  // Progressive — KPIs / leaderboard / unresponded each paint as they
  // arrive. Unresponded scan can be slow on a large lead pool, so this
  // matters most there.
  const fetchAll = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const { date_from, date_to } = periodToRange(period);
    const kick = (url, setter) => fetch(url).then((r) => r.json())
      .then((d) => d?.success && setter(d))
      .catch((e) => console.error(`[RT] ${url} failed:`, e));

    const tasks = [
      kick(`${API}/api/response-time/dashboard-kpis?token=${token}`, (d) => setKpis(d.kpis?.today)),
      kick(`${API}/api/response-time/leaderboard?token=${token}&date_from=${date_from}&date_to=${date_to}&limit=100`, (d) => setLeaderboard(d.leaderboard || [])),
      kick(`${API}/api/response-time/unresponded?token=${token}&hours_threshold=24&limit=50`, (d) => setUnresponded(d.leads || [])),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  }, [token, period]);

  useEffect(() => {
    fetchAll();
    fetchRules();
    const interval = setInterval(fetchAll, 60_000);
    return () => clearInterval(interval);
  }, [fetchAll, fetchRules]);

  // Split leaderboard into direct/indirect using the agent_type field that
  // the service already returns. Fallback bucket for anything unclassified.
  const { directAgents, indirectAgents } = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const matchSearch = (a) =>
      !lower
      || (a.agent_name || '').toLowerCase().includes(lower)
      || (a.agent_email || '').toLowerCase().includes(lower);
    const direct = [];
    const indirect = [];
    leaderboard.forEach((agent) => {
      if (!matchSearch(agent)) return;
      const t = (agent.agent_type || 'direct').toLowerCase();
      if (t === 'indirect') indirect.push(agent);
      else direct.push(agent);
    });
    // Re-rank within each tab so #1 is the best in *that* category.
    const reRank = (list) => list.map((a, i) => ({ ...a, rank: i + 1, badge: i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null }));
    return { directAgents: reRank(direct), indirectAgents: reRank(indirect) };
  }, [leaderboard, search]);

  const renderLeaderboardTable = (rows) => {
    if (!rows.length) {
      return (
        <div className="p-8 text-center text-slate-500 text-sm">
          No agents match the current filter for this time period.
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">Rank</th>
              <th className="text-left px-4 py-3">Agent</th>
              <th className="text-right px-4 py-3">Avg Response</th>
              <th className="text-right px-4 py-3">Fastest</th>
              <th className="text-right px-4 py-3">Slowest</th>
              <th className="text-right px-4 py-3">Total Leads</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((agent) => {
              const c = colorClasses(agent.avg_color);
              return (
                <tr key={agent.agent_id} className={`border-b border-slate-100 ${c.row} hover:bg-slate-50 transition`}>
                  <td className="px-4 py-3 font-semibold text-slate-700 w-16">
                    {agent.badge || `#${agent.rank}`}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={agent.agent_picture} />
                        <AvatarFallback>{(agent.agent_name || '?').charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-slate-900">{agent.agent_name || 'Unknown'}</div>
                        {agent.agent_email && (
                          <div className="text-xs text-slate-500">{agent.agent_email}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Badge className={`${c.bg} ${c.text} ${c.border} border`}>
                      {agent.avg_display || formatHM(agent.avg_response_time)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{formatHM(agent.fastest_response)}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{formatHM(agent.slowest_response)}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">{agent.total_leads}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const range = periodToRange(period);
  const periodLabel = `${fmtDateShort(range.from_obj)} – ${fmtDateShort(range.to_obj)}`;
  const todayLabel = fmtDateShort(new Date());
  const lastRefreshed = fmtDateTime(new Date());

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Timer className="w-6 h-6 text-purple-600" />
            Response Time Tracker
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitor first-response times, SLA compliance, and agent responsiveness across all leads.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Last refreshed {lastRefreshed} · Auto-refresh every 60s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-10 w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchAll} title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Today's KPIs — strictly today (since 00:00 local) */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">
          Today's snapshot · {todayLabel}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label={`Leads assigned (${todayLabel})`} value={kpis?.leads_assigned ?? 0} icon={<Clock className="w-4 h-4 text-blue-500" />} />
          <KpiCard label={`Responded (${todayLabel})`} value={kpis?.leads_responded ?? 0} icon={<CheckCircle className="w-4 h-4 text-emerald-500" />} valueClass="text-emerald-700" />
          <KpiCard
            label={`Avg response time (${todayLabel})`}
            value={kpis?.avg_display || 'No data'}
            icon={<Timer className="w-4 h-4 text-purple-500" />}
            valueClass={colorClasses(kpis?.avg_color).text}
          />
          <KpiCard label={`Response rate (${todayLabel})`} value={`${kpis?.response_rate ?? 0}%`} icon={<Trophy className="w-4 h-4 text-amber-500" />} />
        </div>
      </div>

      {/* Agent leaderboard table — Direct vs Indirect tabs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                Agent Leaderboard
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Showing leads assigned between {periodLabel}
              </p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search agent…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={agentTab} onValueChange={setAgentTab}>
            <TabsList className="mx-4 mt-2">
              <TabsTrigger value="direct">Direct Agents ({directAgents.length})</TabsTrigger>
              <TabsTrigger value="indirect">Indirect Agents ({indirectAgents.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="direct" className="mt-0">
              {renderLeaderboardTable(directAgents)}
            </TabsContent>
            <TabsContent value="indirect" className="mt-0">
              {renderLeaderboardTable(indirectAgents)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Unresponded leads (>24h) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-orange-600">
            <AlertTriangle className="w-4 h-4" />
            Leads Awaiting Response (24h+)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {unresponded.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              All assigned leads have been responded to.
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {unresponded.map((lead) => {
                const c = colorClasses(lead.wait_color);
                return (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition"
                    onClick={() => (window.location.href = `/contacts/${lead.id}`)}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{lead.name || 'Unknown lead'}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {lead.email || lead.phone || 'No contact info'} · {lead.source || 'Unknown source'}
                      </div>
                      {lead.assigned_at && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          Assigned {fmtDateTime(lead.assigned_at)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {slaStatusBadge(lead.sla_status)}
                      <Badge className={`${c.bg} ${c.text} ${c.border} border whitespace-nowrap`} title={`Wait time as of ${lastRefreshed}`}>
                        {lead.wait_display || formatHM(lead.wait_minutes)}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SLA Rules editor */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-purple-600" />
              SLA Rules
            </CardTitle>
            <Button size="sm" onClick={() => setEditingRule({ ...blankRule, __new: true })}>
              <Plus className="w-4 h-4 mr-1" /> New rule
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {rules.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No SLA rules yet. Create one to start classifying leads as Pending → Warning → Breached.
              The first matching rule (by priority) applies; an unconditional catch-all rule is the safest fallback.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Applies to</th>
                    <th className="text-right px-4 py-3">Warning</th>
                    <th className="text-right px-4 py-3">Breach</th>
                    <th className="text-right px-4 py-3">Priority</th>
                    <th className="text-left px-4 py-3">Last updated</th>
                    <th className="text-center px-4 py-3">Active</th>
                    <th className="text-right px-4 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => {
                    const tags = [];
                    if (rule.applies_to_source?.length) tags.push(`source: ${rule.applies_to_source.join(', ')}`);
                    if (rule.applies_to_priority?.length) tags.push(`priority: ${rule.applies_to_priority.join(', ')}`);
                    if (rule.applies_to_department?.length) tags.push(`dept: ${rule.applies_to_department.join(', ')}`);
                    if (!tags.length) tags.push('any lead (catch-all)');
                    return (
                      <tr key={rule.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setEditingRule(rule)}>
                        <td className="px-4 py-3 font-medium text-slate-900">{rule.name}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{tags.join(' · ')}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{formatHM(rule.warning_minutes)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{formatHM(rule.breach_minutes)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{rule.priority_order}</td>
                        <td className="px-4 py-3 text-xs text-slate-500" title={rule.created_at ? `Created ${fmtDateTime(rule.created_at)}` : ''}>
                          {fmtDateTime(rule.updated_at || rule.created_at) || '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {rule.is_active
                            ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-300 border">On</Badge>
                            : <Badge className="bg-slate-100 text-slate-500 border-slate-300 border">Off</Badge>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteRule(rule); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rule editor dialog */}
      {editingRule && (
        <RuleEditorDialog
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={saveRule}
        />
      )}

      {/* Coming-next placeholder so the team sees what's still ahead. */}
      <Card className="border-dashed">
        <CardContent className="py-5 text-sm text-slate-500">
          <span className="font-medium text-slate-700">Coming next:</span>{' '}
          escalation workflow (Level 1-4 + auto-reassignment with audit log),
          source/campaign/property breakdowns, response trend charts, and SLA heatmaps.
        </CardContent>
      </Card>
    </div>
  );
};

const RuleEditorDialog = ({ rule, onClose, onSave }) => {
  const [draft, setDraft] = useState({
    ...rule,
    applies_to_source_csv: (rule.applies_to_source || []).join(', '),
    applies_to_priority_csv: (rule.applies_to_priority || []).join(', '),
    applies_to_department_csv: (rule.applies_to_department || []).join(', '),
  });

  const submit = () => {
    if (!draft.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (Number(draft.warning_minutes) >= Number(draft.breach_minutes)) {
      toast.error('Warning threshold must be lower than breach');
      return;
    }
    onSave({
      ...rule,
      __new: rule.__new,
      name: draft.name.trim(),
      applies_to_source: splitCsv(draft.applies_to_source_csv),
      applies_to_priority: splitCsv(draft.applies_to_priority_csv),
      applies_to_department: splitCsv(draft.applies_to_department_csv),
      warning_minutes: Number(draft.warning_minutes),
      breach_minutes: Number(draft.breach_minutes),
      priority_order: Number(draft.priority_order),
      is_active: !!draft.is_active,
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white p-6">
        <DialogHeader className="pb-4">
          <DialogTitle>{rule.__new ? 'New SLA rule' : 'Edit SLA rule'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. VIP leads · 2-min response" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Warning after (min)</Label>
              <Input type="number" min={1} value={draft.warning_minutes} onChange={(e) => setDraft({ ...draft, warning_minutes: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Breach after (min)</Label>
              <Input type="number" min={1} value={draft.breach_minutes} onChange={(e) => setDraft({ ...draft, breach_minutes: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Source(s) — comma-separated, leave blank to match any</Label>
            <Input value={draft.applies_to_source_csv} onChange={(e) => setDraft({ ...draft, applies_to_source_csv: e.target.value })} placeholder="HubSpot, Website, Facebook Lead Ads" />
          </div>
          <div className="space-y-2">
            <Label>Priority(ies) — e.g. high, vip</Label>
            <Input value={draft.applies_to_priority_csv} onChange={(e) => setDraft({ ...draft, applies_to_priority_csv: e.target.value })} placeholder="high, vip" />
          </div>
          <div className="space-y-2">
            <Label>Department(s) — e.g. sales, support</Label>
            <Input value={draft.applies_to_department_csv} onChange={(e) => setDraft({ ...draft, applies_to_department_csv: e.target.value })} placeholder="sales, support" />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Priority order</Label>
              <p className="text-xs text-slate-500">Higher numbers match first. Use 1000 for VIP overrides, 100 for catch-all.</p>
            </div>
            <Input type="number" className="w-28" value={draft.priority_order} onChange={(e) => setDraft({ ...draft, priority_order: e.target.value })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Active</Label>
            <Switch checked={!!draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} />
          </div>
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit}>{rule.__new ? 'Create' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KpiCard = ({ label, value, icon, valueClass = 'text-slate-900' }) => (
  <Card>
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500">{label}</span>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
    </CardContent>
  </Card>
);

export default ResponseTimeTracker;
